/**
 * Das Land der Organisation vor dem Kauf (Plan API 2026-10-09/02, E1): gelesen aus `organization.current`,
 * gesetzt über `organization.update` — billing besitzt keine Org-Daten.
 *
 * 🔴 **Exakt** `owner`/`admin`, klein: `organization.update` vergleicht `member.role` mit genau diesen zwei
 * Zeichenketten (core-backend 1.117.0, Messung `REQUIREMENTS/API/qa/2026-10-09_plan02-messung-schritt-1.md` (b)).
 * Die Großschreib-Varianten aus `useMemberAdminRights` bekämen dort FORBIDDEN mit englischem Rohtext — für sie
 * zeigt der Dialog den E5-Satz statt eines Aufrufs.
 */

import { useCallback } from 'react'
import type { BuyerCountry, BuyerCountryInput } from '@/pages/protected/BillingPage'
import { trpc } from '@/trpc'

const DARF_LAND_SETZEN = new Set(['owner', 'admin'])

/**
 * `undefined`, solange `organization.current` lädt — die Seite bucht dann wie bisher, und der Server antwortet
 * ohne Land mit `billing-checkout-001` (E8, Rückfall).
 */
export function useBuyerCountry(): BuyerCountry | undefined {
  const utils = trpc.useUtils()
  const { data } = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })
  const update = trpc.organization.update.useMutation({
    // Wer schreibt, entwertet: das Land steht in `organization.current`, von dem auch Sprache, Rechte und
    // VeriFactu-Identität lesen.
    onSuccess: () => utils.organization.current.invalidate(),
  })
  const { mutateAsync } = update
  const setCountry = useCallback(
    async (input: BuyerCountryInput) => {
      await mutateAsync(input)
    },
    [mutateAsync]
  )

  // Kein `useMemo`: in einem Hook mit `useUtils()` hängt ein Memo nur an `utils` (Wächter `apiClientMemoDeps`,
  // Plan CORE-15). Das Objekt ist je Render neu; die Seite führt es nur in `useCallback`-Listen, in keinem Effekt.
  if (!data) return undefined
  return {
    countryCode: data.countryCode,
    canSet: DARF_LAND_SETZEN.has(data.userRole),
    setCountry,
  }
}
